import { React, ts } from '_core';

// Lazy load SwaggerUI
const SwaggerUI = React.lazy(() => import('swagger-ui-react'));

type SwaggerDeferredProps = {
  /** URL to fetch Swagger/OpenAPI spec from */
  url?: string;
  /** Swagger/OpenAPI spec object */
  spec?: ts.types.common.SpecType;
  /** Callback function when Swagger UI finishes loading */
  onComplete?: () => void;
};

/**
 * Component that renders Swagger UI documentation with lazy loading
 *
 * @param url - URL to fetch Swagger/OpenAPI spec from
 * @param spec - Swagger/OpenAPI spec object
 * @param onComplete - Callback function when Swagger UI finishes loading
 * @returns Rendered Swagger UI component
 */
const SwaggerDeferred = ({ url, spec, onComplete }: SwaggerDeferredProps) => {
  const [keyIdx, setKeyIdx] = React.useState(0);

  React.useEffect(() => {
    setKeyIdx((prev) => prev + 1);
  }, [url, spec]);

  // Ensure spec has a valid version field if provided
  const validatedSpec = React.useMemo(() => {
    if (!spec) return undefined;

    // Add default version if missing
    return {
      openapi: '3.1.0',
      ...spec,
    };
  }, [spec]);

  // Memoize the SwaggerUI component to prevent unnecessary re-renders
  const memoizedSwagger = React.useMemo(
    () => (
      <React.Suspense fallback={<div>Loading Swagger UI...</div>}>
        <SwaggerUI key={keyIdx} url={url} spec={validatedSpec} onComplete={onComplete} defaultModelsExpandDepth={-1} />
      </React.Suspense>
    ),
    [url, validatedSpec, onComplete]
  );

  return memoizedSwagger;
};

export default SwaggerDeferred;
